/**
 * What the Clarity loader decides before it loads, and the tags it writes.
 * Pure functions, so the rules are testable without a browser.
 */

/**
 * Tools and robots that run a real browser. Clarity's own bot filter misses
 * them: in Oct 2026 about half the recorded sessions were one-page, few-second
 * visits from European data centres (PageSpeed, uptime monitors, crawlers).
 */
const AUTOMATED_UA =
  /HeadlessChrome|Chrome-Lighthouse|Lighthouse|PTST|GTmetrix|Pingdom|UptimeRobot|StatusCake|Site24x7|bot[/;-]|crawler|spider|Mediapartners/i;
// `bot[/;-]`, not a bare "bot": every crawler names itself `Xbot/1.0` (Googlebot, bingbot, AdsBot-Google,
// YandexBot, AhrefsBot, Applebot), while a phone's model can contain the letters (CUBOT).

/** The production site only. A local production build (`pnpm start`) is NODE_ENV=production too. */
const LIVE_HOSTS = new Set(["nftbaz.com", "www.nftbaz.com"]);

export function isRealVisitor(env: { hostname: string; protocol: string; webdriver: boolean; userAgent: string }): boolean {
  return LIVE_HOSTS.has(env.hostname) && env.protocol === "https:" && !env.webdriver && !AUTOMATED_UA.test(env.userAgent);
}

export type PageType =
  | "home" | "market" | "asset" | "collection" | "artist" | "create_nft" | "create_nft_ai" | "wallet" | "login"
  | "dashboard" | "settings" | "search" | "blog" | "ticket" | "message" | "auction" | "bids" | "drop" | "gallery"
  | "saved" | "notifications" | "portfolio" | "currency" | "other";

const BY_SEGMENT: Record<string, PageType> = {
  market: "market",
  assets: "asset",
  collection: "collection",
  "special-collections": "collection",
  artists: "artist",
  wallet: "wallet",
  redeem: "wallet",
  auth: "login",
  "connect-wallet": "login",
  dashboard: "dashboard",
  settings: "settings",
  search: "search",
  blogs: "blog",
  ticket: "ticket",
  message: "message",
  auctions: "auction",
  "active-bid": "auction",
  bids: "bids",
  drops: "drop",
  galleries: "gallery",
  saved: "saved",
  notifications: "notifications",
  portfolio: "portfolio",
  currencies: "currency",
};

/** The kind of page a pathname is, ignoring its locale: `/fa/create-nft/ai/12` → "create_nft_ai". */
export function pageTypeOf(pathname: string): PageType {
  const [, , first = "", second = ""] = pathname.split("/");
  if (!first) return "home";
  if (first === "create-nft") return second === "ai" ? "create_nft_ai" : "create_nft";
  return BY_SEGMENT[first] ?? "other";
}

/** A tag value Clarity keeps whole: web-vitals selectors can run long. */
export function tagValue(value: string): string {
  return value.length > 200 ? `${value.slice(0, 199)}…` : value;
}
